import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LOCALES } from "./i18n";
import ruloPicture from "./assets/rulo_hesaplama_new.png";

const INCH_TO_MM = 25.4;
const KG_TO_LB = 2.2046226218;
const KG_M3_TO_LB_IN3 = 0.000036127292;
const KG_PER_MM_TO_LB_PER_IN = KG_TO_LB * INCH_TO_MM;

export default function CoilWeightCalculator({ t, lang, unitSystem = "metric" }) {
    const MATERIALS = useMemo(
        () => [
            { key: "steel", name: t.matSteel, density: 7850 },
            { key: "stainless", name: t.matStainless, density: 8000 },
            { key: "aluminum", name: t.matAluminum, density: 2700 },
            { key: "copper", name: t.matCopper, density: 8960 },
            { key: "brass", name: t.matBrass, density: 8500 },
            { key: "cast_iron", name: t.matCastIron, density: 7200 },
            { key: "pp", name: t.matPP, density: 905 },
            { key: "pe", name: t.matPE, density: 950 },
            { key: "pvc", name: t.matPVC, density: 1380 },
            { key: "custom", name: t.matCustom, density: null },
        ],
        [t]
    );

    const [materialKey, setMaterialKey] = useState("");
    const [density, setDensity] = useState("");
    const [thicknessMm, setThicknessMm] = useState("");
    const [widthMm, setWidthMm] = useState("");
    const [innerDiameterMm, setInnerDiameterMm] = useState("");
    const [outerDiameterMm, setOuterDiameterMm] = useState("");
    const [weightKg, setWeightKg] = useState("");
    const [lengthM, setLengthM] = useState("");
    const [weightPerMmWidth, setWeightPerMmWidth] = useState("");
    const [error, setError] = useState("");
    const previousUnitSystem = useRef(unitSystem);

    const selectedMaterial = MATERIALS.find((m) => m.key === materialKey);
    const isImperial = unitSystem === "imperial";
    const lengthUnit = isImperial ? "in" : "mm";
    const weightUnit = isImperial ? "lb" : "kg";
    const densityUnit = isImperial ? "lb/in³" : "kg/m³";
    const weightPerWidthUnit = isImperial ? "lb/in" : "kg/mm";

    const parseNumber = useCallback((value) => {
        const normalized = String(value ?? "")
            .trim()
            .replace(/\s+/g, "")
            .replace(",", ".");

        if (!normalized) return NaN;

        const number = Number(normalized);
        return Number.isFinite(number) ? number : NaN;
    }, []);

    const formatNumber = useCallback((value, decimals = 2) => {
        if (!Number.isFinite(value)) return "";

        const rounded = Number(value.toFixed(decimals));

        return rounded.toLocaleString(LOCALES[lang] || "tr-TR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals,
        });
    }, [lang]);

    const clearCalculated = () => {
        setError("");
    };

    const convertDisplayValue = useCallback((value, converter, decimals = 4) => {
        const number = parseNumber(value);
        return Number.isFinite(number) ? formatNumber(converter(number), decimals) : value;
    }, [formatNumber, parseNumber]);

    const fromMetricLength = (value) => isImperial ? value / INCH_TO_MM : value;
    const toMetricLength = (value) => isImperial ? value * INCH_TO_MM : value;
    const fromMetricWeight = (value) => isImperial ? value * KG_TO_LB : value;
    const toMetricWeight = (value) => isImperial ? value / KG_TO_LB : value;
    const fromMetricDensity = (value) => isImperial ? value * KG_M3_TO_LB_IN3 : value;
    const toMetricDensity = (value) => isImperial ? value / KG_M3_TO_LB_IN3 : value;
    const fromMetricWeightPerWidth = (value) => isImperial ? value * KG_PER_MM_TO_LB_PER_IN : value;
    const toMetricWeightPerWidth = (value) => isImperial ? value / KG_PER_MM_TO_LB_PER_IN : value;

    const onSelectMaterial = (key) => {
        setMaterialKey(key);

        const material = MATERIALS.find((m) => m.key === key);

        if (!material) return;

        if (material.key === "custom") {
            setDensity("");
        } else {
            setDensity(formatNumber(fromMetricDensity(material.density), isImperial ? 4 : 0));
        }

        setError("");
    };

    useEffect(() => {
        const previous = previousUnitSystem.current;
        if (previous === unitSystem) return;

        const wasImperial = previous === "imperial";
        const fromPreviousLength = (value) => wasImperial ? value * INCH_TO_MM : value;
        const toCurrentLength = (value) => isImperial ? value / INCH_TO_MM : value;
        const fromPreviousWeight = (value) => wasImperial ? value / KG_TO_LB : value;
        const toCurrentWeight = (value) => isImperial ? value * KG_TO_LB : value;
        const fromPreviousDensity = (value) => wasImperial ? value / KG_M3_TO_LB_IN3 : value;
        const toCurrentDensity = (value) => isImperial ? value * KG_M3_TO_LB_IN3 : value;
        const fromPreviousWeightPerWidth = (value) => wasImperial ? value / KG_PER_MM_TO_LB_PER_IN : value;
        const toCurrentWeightPerWidth = (value) => isImperial ? value * KG_PER_MM_TO_LB_PER_IN : value;

        setThicknessMm((value) => convertDisplayValue(value, (n) => toCurrentLength(fromPreviousLength(n)), isImperial ? 3 : 2));
        setWidthMm((value) => convertDisplayValue(value, (n) => toCurrentLength(fromPreviousLength(n)), isImperial ? 3 : 2));
        setInnerDiameterMm((value) => convertDisplayValue(value, (n) => toCurrentLength(fromPreviousLength(n)), isImperial ? 3 : 2));
        setOuterDiameterMm((value) => convertDisplayValue(value, (n) => toCurrentLength(fromPreviousLength(n)), isImperial ? 3 : 2));
        setLengthM((value) => convertDisplayValue(value, (n) => toCurrentLength(fromPreviousLength(n)), isImperial ? 3 : 2));
        setWeightKg((value) => convertDisplayValue(value, (n) => toCurrentWeight(fromPreviousWeight(n)), 2));
        setWeightPerMmWidth((value) => convertDisplayValue(value, (n) => toCurrentWeightPerWidth(fromPreviousWeightPerWidth(n)), 4));

        const material = MATERIALS.find((m) => m.key === materialKey);
        if (material && material.key !== "custom") {
            setDensity(formatNumber(toCurrentDensity(material.density), isImperial ? 4 : 0));
        } else {
            setDensity((value) => convertDisplayValue(value, (n) => toCurrentDensity(fromPreviousDensity(n)), isImperial ? 4 : 0));
        }

        setError("");
        previousUnitSystem.current = unitSystem;
    }, [MATERIALS, convertDisplayValue, formatNumber, isImperial, materialKey, unitSystem]);

    const reset = () => {
        setMaterialKey("");
        setDensity("");
        setThicknessMm("");
        setWidthMm("");
        setInnerDiameterMm("");
        setOuterDiameterMm("");
        setWeightKg("");
        setLengthM("");
        setWeightPerMmWidth("");
        setError("");
    };

    const validateRequired = ({ dens, thickness, width, innerDiameter }) => {
        if (!Number.isFinite(dens) || dens <= 0) {
            return t.errDensity;
        }

        if (!Number.isFinite(thickness) || thickness <= 0) {
            return t.errThickness;
        }

        if (!Number.isFinite(width) || width <= 0) {
            return t.errWidth;
        }

        if (!Number.isFinite(innerDiameter) || innerDiameter <= 0) {
            return t.errInnerDia;
        }

        return "";
    };

    const calculateFromOuterDiameter = ({ dens, thickness, width, innerDiameter, outerDiameter }) => {
        if (!Number.isFinite(outerDiameter) || outerDiameter <= 0) {
            throw new Error(t.errOuterDia);
        }

        if (outerDiameter <= innerDiameter) {
            throw new Error(t.errOuterGtInner);
        }

        const rOutM = outerDiameter / 2000;
        const rInM = innerDiameter / 2000;
        const widthM = width / 1000;

        const volumeM3 = Math.PI * (rOutM * rOutM - rInM * rInM) * widthM;
        const weight = volumeM3 * dens;

        const length =
            (Math.PI * (outerDiameter * outerDiameter - innerDiameter * innerDiameter)) /
            (4 * thickness);

        const kgPerMm = weight / width;

        return { outerDiameter, weight, length, kgPerMm };
    };

    const calculateFromWeight = ({ dens, thickness, width, innerDiameter, weight }) => {
        if (!Number.isFinite(weight) || weight <= 0) {
            throw new Error(t.errWeight);
        }

        const widthM = width / 1000;
        const rInM = innerDiameter / 2000;

        const rOutM = Math.sqrt(weight / (dens * Math.PI * widthM) + rInM * rInM);
        const outerDiameter = rOutM * 2000;

        const length = (weight * 1000000000) / (dens * thickness * width);
        const kgPerMm = weight / width;

        return { outerDiameter, weight, length, kgPerMm };
    };

    const calculateFromLength = ({ dens, thickness, width, innerDiameter, length }) => {
        if (!Number.isFinite(length) || length <= 0) {
            throw new Error(t.errLength);
        }

        const weight = (length * dens * thickness * width) / 1000000000;

        const outerDiameter = Math.sqrt(
            (4 * thickness * length) / Math.PI +
            innerDiameter * innerDiameter
        );

        const kgPerMm = weight / width;

        return { outerDiameter, weight, length, kgPerMm };
    };

    const calculateFromWeightPerMmWidth = ({
                                               dens,
                                               thickness,
                                               width,
                                               innerDiameter,
                                               kgPerMm,
                                           }) => {
        if (!Number.isFinite(kgPerMm) || kgPerMm <= 0) {
            throw new Error(t.errKgPerMm);
        }

        const weight = kgPerMm * width;

        return calculateFromWeight({
            dens,
            thickness,
            width,
            innerDiameter,
            weight,
        });
    };

    const calculate = () => {
        setError("");

        const dens = toMetricDensity(parseNumber(density));
        const thickness = toMetricLength(parseNumber(thicknessMm));
        const width = toMetricLength(parseNumber(widthMm));
        const innerDiameter = toMetricLength(parseNumber(innerDiameterMm));
        const outerDiameter = toMetricLength(parseNumber(outerDiameterMm));
        const weight = toMetricWeight(parseNumber(weightKg));
        const length = toMetricLength(parseNumber(lengthM));
        const kgPerMm = toMetricWeightPerWidth(parseNumber(weightPerMmWidth));

        const requiredError = validateRequired({
            dens,
            thickness,
            width,
            innerDiameter,
        });

        if (requiredError) {
            setError(requiredError);
            return;
        }

        const filledOptionals = [
            Number.isFinite(outerDiameter) && outerDiameter > 0,
            Number.isFinite(weight) && weight > 0,
            Number.isFinite(length) && length > 0,
            Number.isFinite(kgPerMm) && kgPerMm > 0,
        ].filter(Boolean).length;

        if (filledOptionals === 0) {
            setError(t.errNeedOne);
            return;
        }

        if (filledOptionals === 4) {
            setError(t.errAllFilled);
            return;
        }

        try {
            let result;

            if (Number.isFinite(outerDiameter) && outerDiameter > 0) {
                result = calculateFromOuterDiameter({
                    dens,
                    thickness,
                    width,
                    innerDiameter,
                    outerDiameter,
                });
            } else if (Number.isFinite(weight) && weight > 0) {
                result = calculateFromWeight({
                    dens,
                    thickness,
                    width,
                    innerDiameter,
                    weight,
                });
            } else if (Number.isFinite(length) && length > 0) {
                result = calculateFromLength({
                    dens,
                    thickness,
                    width,
                    innerDiameter,
                    length,
                });
            } else {
                result = calculateFromWeightPerMmWidth({
                    dens,
                    thickness,
                    width,
                    innerDiameter,
                    kgPerMm,
                });
            }

            setOuterDiameterMm(formatNumber(fromMetricLength(result.outerDiameter), isImperial ? 3 : 2));
            setWeightKg(formatNumber(fromMetricWeight(result.weight), 2));
            setLengthM(formatNumber(fromMetricLength(result.length), isImperial ? 3 : 2));
            setWeightPerMmWidth(formatNumber(fromMetricWeightPerWidth(result.kgPerMm), 4));
        } catch (err) {
            setError(err.message || t.errCalc);
        }
    };

    const isDensityLocked =
        selectedMaterial && selectedMaterial.key && selectedMaterial.key !== "custom";

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div className="coil-card-row" style={styles.cardRow}>
                <div className="coil-form-panel" style={styles.inner}>
                <div style={styles.title}>{t.coilTitle}</div>

                <div style={styles.row}>
                    <div style={styles.label}>{t.material}</div>
                    <div style={styles.unit}></div>
                    <div style={styles.inputWrap}>
                        <select
                            value={materialKey}
                            onChange={(e) => onSelectMaterial(e.target.value)}
                            style={styles.select}
                        >
                            <option value="" disabled>
                                {t.select}
                            </option>
                            {MATERIALS.map((material) => (
                                <option key={material.key} value={material.key}>
                                    {material.name}
                                    {material.density ? ` (${formatNumber(fromMetricDensity(material.density), isImperial ? 4 : 0)})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <Field
                    label={t.density}
                    unit={densityUnit}
                    value={density}
                    disabled={isDensityLocked}
                    onChange={(v) => {
                        setDensity(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.thickness}
                    unit={lengthUnit}
                    value={thicknessMm}
                    onChange={(v) => {
                        setThicknessMm(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.width}
                    unit={lengthUnit}
                    value={widthMm}
                    onChange={(v) => {
                        setWidthMm(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.innerDiameter}
                    unit={lengthUnit}
                    value={innerDiameterMm}
                    onChange={(v) => {
                        setInnerDiameterMm(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.outerDiameter}
                    unit={lengthUnit}
                    value={outerDiameterMm}
                    onChange={(v) => {
                        setOuterDiameterMm(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.weight}
                    unit={weightUnit}
                    value={weightKg}
                    onChange={(v) => {
                        setWeightKg(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.length}
                    unit={lengthUnit}
                    value={lengthM}
                    onChange={(v) => {
                        setLengthM(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={weightPerWidthUnit}
                    unit=""
                    value={weightPerMmWidth}
                    onChange={(v) => {
                        setWeightPerMmWidth(v);
                        clearCalculated();
                    }}
                />

                {error ? <div style={styles.error}>{error}</div> : null}

                <div style={styles.actions}>
                    <button type="button" onClick={reset} style={styles.btnSecondary}>
                        {t.clear}
                    </button>

                    <button type="button" onClick={calculate} style={styles.btnPrimary}>
                        {t.calculate}
                    </button>
                </div>
                </div>

                {/* Rulo görseli - ölçü çizgileri ve etiketler görsel üzerinde */}
                <div className="coil-picture-panel flex justify-center">
                    <div className="relative w-[340px] max-w-full">
                        <img src={ruloPicture} alt="" className="block w-full h-auto" />
                        <span className="absolute left-[18%] top-[8%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.weight} [{weightUnit}]
                        </span>
                        <span className="absolute left-[-2%] top-[36%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.outerDiameter} [{lengthUnit}]
                        </span>
                        <span className="absolute left-[2%] top-[73%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.innerDiameter} [{lengthUnit}]
                        </span>
                        <span className="absolute left-[68%] top-[43%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.length} [{lengthUnit}]
                        </span>
                        <span className="absolute left-[72%] top-[94%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.width} [{lengthUnit}]
                        </span>
                        <span className="absolute left-[84%] top-[75%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.thickness} [{lengthUnit}]
                        </span>
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, unit, value, onChange, disabled = false }) {
    const adjustValue = (delta) => {
        const normalized = String(value ?? "").trim().replace(/\s+/g, "").replace(",", ".");
        const current = Number(normalized);
        const next = (Number.isFinite(current) ? current : 0) + delta;
        onChange(String(Number(next.toFixed(4))));
    };

    return (
        <div style={styles.row}>
            <div style={styles.label}>{label}</div>
            <div style={styles.unit}>{unit}</div>
            <div style={styles.inputWrap}>
                <div style={styles.stepper}>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => adjustValue(1)}
                        style={{
                            ...styles.stepperButton,
                            ...(disabled ? styles.stepperButtonDisabled : {}),
                        }}
                        aria-label={`${label} artır`}
                    >
                        +
                    </button>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => adjustValue(-1)}
                        style={{
                            ...styles.stepperButton,
                            ...(disabled ? styles.stepperButtonDisabled : {}),
                        }}
                        aria-label={`${label} azalt`}
                    >
                        -
                    </button>
                </div>
                <input
                    inputMode="decimal"
                    placeholder="—"
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        ...styles.input,
                        ...(disabled ? styles.inputDisabled : {}),
                    }}
                />
            </div>
        </div>
    );
}

const styles = {
    page: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        padding: "8px 0",
    },

    card: {
        width: "100%",
        background: "#ffffff",
        borderRadius: 16,
        padding: 16,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    },

    // Form solda, rulo görseli sağda
    cardRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        alignItems: "center",
    },

    inner: {
        flex: "1 1 320px",
        maxWidth: 640,
    },

    pictureWrap: {
        position: "relative",
        flex: "0 1 560px",
        maxWidth: 560,
        alignSelf: "center",
    },

    picture: {
        width: "100%",
        height: "auto",
        display: "block",
    },

    overlay: {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
    },

    imgLabel: {
        position: "absolute",
        fontSize: 12,
        fontWeight: 700,
        color: "#1d1d1b",
        whiteSpace: "nowrap",
        background: "rgba(255, 255, 255, 0.86)",
        borderRadius: 4,
        padding: "2px 6px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.16)",
        pointerEvents: "none",
    },

    title: {
        fontSize: 16,
        fontWeight: 700,
        marginBottom: 12,
        color: "#222",
    },

    row: {
        display: "grid",
        gridTemplateColumns: "minmax(92px, 0.9fr) minmax(34px, 48px) minmax(150px, 1.45fr)",
        gap: 6,
        alignItems: "center",
        padding: "9px 10px",
        borderRadius: 12,
        background: "#f2f2f2",
        marginBottom: 10,
    },

    label: {
        fontSize: 14,
        color: "#555",
        fontWeight: 600,
    },

    unit: {
        fontSize: 13,
        color: "#777",
        textAlign: "left",
        fontWeight: 600,
    },

    inputWrap: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        position: "relative",
    },

    input: {
        width: "100%",
        height: 38,
        borderRadius: 10,
        border: "1px solid #e1e1e1",
        padding: "0 28px 0 58px",
        fontSize: 14,
        outline: "none",
        background: "#fff",
        textAlign: "right",
        boxSizing: "border-box",
    },

    stepper: {
        position: "absolute",
        left: 4,
        top: "50%",
        transform: "translateY(-50%)",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 2,
        zIndex: 1,
    },

    stepperButton: {
        width: 22,
        height: 28,
        borderRadius: 7,
        border: "1px solid #d8d8d8",
        background: "#f6f6f6",
        color: "#555",
        fontSize: 13,
        lineHeight: "13px",
        fontWeight: 800,
        cursor: "pointer",
        padding: 0,
    },

    stepperButtonDisabled: {
        color: "#bbb",
        background: "#f0f0f0",
        cursor: "not-allowed",
    },

    inputDisabled: {
        background: "#f8f8f8",
        color: "#666",
    },

    select: {
        width: "100%",
        height: 38,
        borderRadius: 10,
        border: "1px solid #e1e1e1",
        padding: "0 12px",
        fontSize: 14,
        outline: "none",
        background: "#fff",
        boxSizing: "border-box",
    },

    actions: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        marginTop: 12,
    },

    btnSecondary: {
        height: 44,
        borderRadius: 12,
        border: "1px solid #ddd",
        background: "#ececec",
        fontWeight: 700,
        color: "#666",
        cursor: "pointer",
    },

    btnPrimary: {
        height: 44,
        borderRadius: 12,
        border: "none",
        background: "#ff5a00",
        fontWeight: 800,
        color: "#fff",
        cursor: "pointer",
    },

    error: {
        marginTop: 8,
        padding: "10px 12px",
        background: "#fff1f0",
        border: "1px solid #ffccc7",
        borderRadius: 12,
        color: "#a8071a",
        fontSize: 13,
        fontWeight: 600,
    },
};
